import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ScratchCard from '../components/ScratchCard';
import './Game.css';

const IMAGE_BASE_URL = process.env.REACT_APP_ASSET_BASE_URL || window.location.origin;
const FINAL_REVEAL_HOLD_MS = 2500;
const PRIZE_IMAGES = {
  bouteille_035: 'AC-035.jpg.jpeg',
  bouteille_120: 'AC-120.jpg.jpeg',
  ballon_basket: 'Balle-AC.jpg.jpeg',
  casquette: 'Casquette-Ac.jpg.jpeg',
  casque_bluetooth: 'Casque-AC.jpg.jpeg',
  gobelet: 'Gobelet-AC.jpg (1).jpeg',
  tenue_basket: 'tenu-basket.jpg (1).jpeg',
  pull: 'pull-AC.jpg.jpeg',
  try_again_1: null,
  reessayer: null
};

const LABEL_IMAGE_MAP = {
  'american cola 0 35l': 'AC-035.jpg.jpeg',
  'american cola 1 20l': 'AC-120.jpg.jpeg',
  'bouteille 0 35l': 'AC-035.jpg.jpeg',
  'bouteille 1 20l': 'AC-120.jpg.jpeg',
  'casque bluetooth premium': 'Casque-AC.jpg.jpeg',
  'casque bluetooth': 'Casque-AC.jpg.jpeg',
  'tenue de basket american cola': 'tenu-basket.jpg (1).jpeg',
  'tenue de basket': 'tenu-basket.jpg (1).jpeg',
  'ballon de basket premium': 'Balle-AC.jpg.jpeg',
  'ballon de basket': 'Balle-AC.jpg.jpeg',
  'casquette american cola': 'Casquette-Ac.jpg.jpeg',
  'casquette': 'Casquette-Ac.jpg.jpeg',
  'gobelet american cola': 'Gobelet-AC.jpg (1).jpeg',
  'gobelet': 'Gobelet-AC.jpg (1).jpeg'
};

const normalizeKey = (value) => {
  if (!value) return '';
  return value
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const normalizeAssetUrl = (value) => {
  if (!value) return null;
  const trimmed = value.toString().trim();
  if (!trimmed) return null;
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('data:')) {
    return trimmed;
  }
  return `${IMAGE_BASE_URL}/${encodeURIComponent(trimmed)}`;
};

const getPrizeImageUrl = (prizeType, prizeLabel, prizeName) => {
  const typeKey = normalizeKey(prizeType).replace(/ /g, '_');
  const direct = PRIZE_IMAGES[prizeType] || PRIZE_IMAGES[typeKey];
  const labelKey = normalizeKey(prizeLabel);
  const nameKey = normalizeKey(prizeName);
  const filename = direct || LABEL_IMAGE_MAP[labelKey] || LABEL_IMAGE_MAP[nameKey];
  if (!filename) return null;
  return `${IMAGE_BASE_URL}/${encodeURIComponent(filename)}`;
};

const Game = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState(null);
  const [playMode, setPlayMode] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [currentPrizeIndex, setCurrentPrizeIndex] = useState(0);
  const [revealedPrizes, setRevealedPrizes] = useState([]);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const timeoutRef = useRef(null);
  const finalizeTimeoutRef = useRef(null);

  const verifyToken = useCallback(async () => {
    try {
      const response = await api.get(`/game/verify/${token}`);
      if (response.data.valid) {
        setPlayMode(response.data.play_mode || null);

        if (response.data.predetermined_prizes && response.data.predetermined_prizes.length > 0) {
          setPrizes(response.data.predetermined_prizes);

          const firstUnrevealed = response.data.predetermined_prizes.findIndex(p => !p.revealed);
          if (firstUnrevealed >= 0) {
            setCurrentPrizeIndex(firstUnrevealed);
          }

          const alreadyRevealed = response.data.predetermined_prizes
            .filter(p => p.revealed)
            .map(p => ({
              ...p,
              revealed_at: p.played_at
            }));
          setRevealedPrizes(alreadyRevealed);
        }
      } else {
        setError(response.data.error || 'Token invalide');
      }
    } catch (err) {
      console.error('Erreur vérification token:', err);
      setError('Erreur lors de la vérification du token');
    } finally {
      setValidating(false);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (finalizeTimeoutRef.current) {
        clearTimeout(finalizeTimeoutRef.current);
      }
    };
  }, [token, verifyToken]);

  const handleRevealComplete = () => {
    setIsRevealed(true);

    const currentPrize = prizes[currentPrizeIndex];
    markPrizeAsPlayed();

    const commitReveal = () => {
      setRevealedPrizes(prev => [
        ...prev,
        {
          ...currentPrize,
          revealed_at: new Date().toISOString()
        }
      ]);
    };

    const isLastCard = currentPrizeIndex >= prizes.length - 1;
    if (isLastCard) {
      finalizeTimeoutRef.current = setTimeout(commitReveal, FINAL_REVEAL_HOLD_MS);
      return;
    }

    commitReveal();
  };

  const markPrizeAsPlayed = async () => {
    try {
      await api.post(`/game/mark-played/${token}`, {
        play_number: prizes[currentPrizeIndex].play_number
      });
    } catch (err) {
      console.error('Erreur marquage lot joué:', err);
    }
  };

  useEffect(() => {
    if (isRevealed && currentPrizeIndex < prizes.length - 1) {
      setIsResetting(true);
      timeoutRef.current = setTimeout(() => {
        setCurrentPrizeIndex(prev => prev + 1);
        setIsRevealed(false);
        setIsResetting(false);
      }, 3000);
    }
  }, [isRevealed, currentPrizeIndex, prizes.length]);

  const currentPrize = prizes[currentPrizeIndex];
  const allRevealed = revealedPrizes.length === prizes.length && prizes.length > 0;
  const remainingCards = prizes.length - revealedPrizes.length;

  const isWinnerPrize = (prize) => {
    if (!prize.prize_code) return false;
    const losingTypes = ['try_again', 'reessayer', 'perdu', 'lose'];
    return !losingTypes.some(type => prize.prize_type.toLowerCase().includes(type));
  };

  const getPrizeThumbUrl = (prize) => {
    if (!prize) return null;
    const iconUrl = normalizeAssetUrl(prize.icon);
    if (iconUrl) return iconUrl;
    return getPrizeImageUrl(prize.prize_type, prize.prize_label, prize.name || prize.prize_name);
  };

  const winningPrizes = revealedPrizes.filter(isWinnerPrize);
  const hasWinningPrizes = winningPrizes.length > 0;

  if (loading || validating) {
    return (
      <div className="game-page-container">
        <div className="game-loading-container">
          <div className="game-spinner"></div>
          <p className="game-loading-text">Chargement de votre carte à gratter...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-page-container">
        <div className="game-error-container">
          <div className="game-error-icon">❌</div>
          <h2 className="game-error-title">Erreur</h2>
          <p className="game-error-message">{error}</p>
          {error.includes('déjà utilisé') && (
            <p className="game-error-help">Ce lien a déjà été utilisé. Veuillez contacter une hôtesse.</p>
          )}
          {error.includes('expiré') && (
            <p className="game-error-help">Ce lien a expiré. Veuillez contacter une hôtesse.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="game-page-container">
      {/* Header */}
      <nav className="game-navbar">
        <div className="game-navbar-content">
          <img src="/logo.png" alt="American Cola" className="game-logo" />
          {playMode === 'direct' ? (
            <button
              onClick={() => navigate('/hostess')}
              className="game-back-button"
            >
              <i className="fas fa-arrow-left me-2"></i>
              <span>Retour</span>
            </button>
          ) : null}
        </div>
      </nav>

      {/* Main Content */}
      <div className="game-content">
        {!allRevealed && currentPrize && (
          <>
            {/* Instructions Card */}
            <div className="game-card game-instructions-card">
              <div className="game-card-header">
                <div className="game-card-icon">
                  <i className="fas fa-ticket-alt"></i>
                </div>
                <div className="game-card-title-group">
                  <h2 className="game-card-title">Grattez votre carte</h2>
                  <p className="game-card-subtitle">Utilisez votre doigt pour révéler votre lot !</p>
                </div>
              </div>
              {remainingCards > 0 && (
                <div className="game-progress-bar">
                  <div className="game-progress-info">
                    <span className="game-progress-label">Carte {currentPrizeIndex + 1} sur {prizes.length}</span>
                    <span className="game-progress-remaining">{remainingCards > 1 ? `${remainingCards} restantes` : 'Dernière !'}</span>
                  </div>
                  <div className="game-progress-track">
                    <div
                      className="game-progress-fill"
                      style={{ width: `${((revealedPrizes.length + (isRevealed ? 1 : 0)) / prizes.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Scratch Card */}
            <div className="game-scratch-wrapper">
              <ScratchCard
                key={currentPrizeIndex}
                prize={{
                  name: currentPrize.prize_label,
                  prize_label: currentPrize.prize_label,
                  prize_type: currentPrize.prize_type,
                  prize_code: currentPrize.prize_code,
                  image_url: getPrizeImageUrl(currentPrize.prize_type, currentPrize.prize_label, currentPrize.name),
                  icon: currentPrize.icon
                }}
                onReveal={handleRevealComplete}
                isRevealed={isRevealed}
              />
            </div>

            {/* Reset Message */}
            {isResetting && (
              <div className="game-reset-message">
                <div className="game-reset-icon">🎉</div>
                <p>Préparation de la prochaine carte...</p>
              </div>
            )}
          </>
        )}

        {/* History - remonté juste après la carte */}
        {revealedPrizes.length > 0 && (
          <div className="game-card game-history-card">
            <div className="game-card-header">
              <div className="game-card-icon game-icon-gold">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="game-card-title-group">
                <h3 className="game-card-title">Vos lots révélés</h3>
                <p className="game-card-subtitle">{revealedPrizes.length} / {prizes.length}</p>
              </div>
            </div>
            <div className="game-history-list">
              {revealedPrizes.map((prize, idx) => {
                const isWinner = isWinnerPrize(prize);
                const thumbUrl = isWinner ? getPrizeThumbUrl(prize) : null;
                return (
                  <div key={idx} className={`game-history-item ${isWinner ? 'winner' : 'loser'}`}>
                    <div className={`game-history-number ${isWinner ? 'winner' : 'loser'}`}>
                      #{prize.play_number}
                    </div>
                    <div className="game-history-content">
                      <div className="game-history-label">{prize.prize_label}</div>
                      {prize.prize_code && (
                        <div className="game-history-code">Code: {prize.prize_code}</div>
                      )}
                    </div>
                    <div className={`game-history-icon ${isWinner ? 'winner' : 'loser'}`}>
                      {isWinner && thumbUrl ? (
                        <img src={thumbUrl} alt={prize.prize_label || 'Lot'} loading="lazy" />
                      ) : isWinner ? (
                        '🎁'
                      ) : (
                        '😢'
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Complete Message */}
        {allRevealed && (
          <div className={`game-card game-complete-card ${hasWinningPrizes ? 'winner' : 'loser'}`}>
            {hasWinningPrizes ? (
              <>
                <div className="game-complete-icon winner">
                  <i className="fas fa-glass-cheers"></i>
                </div>
                <h2 className="game-complete-title winner">Félicitations !</h2>
                <p className="game-complete-text">Vous avez gagné {winningPrizes.length} lot{winningPrizes.length > 1 ? 's' : ''} !</p>
                <div className="game-winning-list">
                  {winningPrizes.map((prize, idx) => {
                    const thumbUrl = getPrizeThumbUrl(prize);
                    return (
                      <div key={idx} className="game-winning-item">
                        <span className="game-winning-icon">
                          {thumbUrl ? (
                            <img src={thumbUrl} alt={prize.prize_label || 'Lot'} loading="lazy" />
                          ) : (
                            '🎁'
                          )}
                        </span>
                        <span className="game-winning-label">{prize.prize_label}</span>
                        {prize.prize_code && (
                          <span className="game-winning-code">Code: {prize.prize_code}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="game-complete-action winner">
                  <i className="fas fa-gift me-2"></i>
                  Présentez vos codes à une hôtesse
                </div>
              </>
            ) : (
              <>
                <div className="game-complete-icon loser">
                  <i className="fas fa-heart-broken"></i>
                </div>
                <h2 className="game-complete-title loser">Dommage !</h2>
                <p className="game-complete-text">Vous n'avez pas gagné cette fois-ci.</p>
                <div className="game-complete-action loser">
                  <i className="fas fa-redo me-2"></i>
                  Achetez plus de bouteilles et retentez votre chance !
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="game-footer">
        <p>© 2026 American Cola</p>
      </footer>
    </div>
  );
};

export default Game;
